import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import type { BrandingResponse, LoginResponseRaw, UserProfile } from '@/types/api';
import type { LoginInput } from './schema';

/* --------------------------------------------------------------- *
 * Branding — public endpoint that returns company name + logo URL  *
 * for the current host.                                            *
 * --------------------------------------------------------------- */

export function useBranding() {
  return useQuery({
    queryKey: ['branding', window.location.hostname],
    queryFn: () => api.get<BrandingResponse>(`/api/obd/branding/${window.location.hostname}`),
    /* Cache aggressively — branding rarely changes. */
    staleTime: 60 * 60 * 1000,
    /* Even an empty response is fine, falls back to defaults. */
    retry: false,
  });
}

/* --------------------------------------------------------------- *
 * Login                                                            *
 * --------------------------------------------------------------- */

interface LoginSuccess {
  userId: string;
  token: string;
  role: string;
}

/**
 * Legacy java_backend contract:
 *   POST /api/obd/login   body: { username, password }
 *   200 -> { userid, token, role: string[] }   ← note lowercase `userid`
 *   401 -> { message: 'Bad credentials' }
 *
 * We normalize to camelCase internally so feature code reads `userId`.
 */
export function useLoginMutation() {
  const loginToStore = useAuthStore((s) => s.login);
  return useMutation({
    mutationFn: async (input: LoginInput): Promise<LoginSuccess> => {
      const raw = await api.post<LoginResponseRaw>('/api/obd/login', input, {
        // Force no Authorization on /login even if a stale token is around.
        transformRequest: [(data, headers) => {
          if (headers) delete (headers as Record<string, unknown>).Authorization;
          return JSON.stringify(data);
        }],
      });
      if (!raw.userid || !raw.token || !raw.role?.length) {
        throw new Error(raw.message ?? 'Login failed');
      }
      const role = raw.role[0];
      if (!role) throw new Error('Login response missing role');
      return { userId: raw.userid, token: raw.token, role };
    },
    onSuccess: (data) => {
      loginToStore({
        user: { userId: data.userId, role: data.role },
        token: data.token,
      });
    },
  });
}

/* --------------------------------------------------------------- *
 * User profile — runs after login to populate username/balance/etc *
 * --------------------------------------------------------------- */

export function useUserProfile() {
  const userId = useAuthStore((s) => s.userId);
  const hydrateProfile = useAuthStore((s) => s.hydrateProfile);
  return useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const profile = await api.get<UserProfile>(`/api/obd/user/profile/${userId}`);
      hydrateProfile({ username: profile.username, name: profile.name });
      return profile;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
}

/* --------------------------------------------------------------- *
 * Per-user UI settings (sidebar / theme prefs)                     *
 *   GET  /api/obd/user/{userId}/ui/settings → { json: string|null} *
 *   POST /api/obd/user/{userId}/ui/settings   body { json: string }*
 *                                                                  *
 * Persisted on the user_details.ui_settings column. The frontend   *
 * hydrates the Zustand theme store from the GET response after    *
 * login and pushes any subsequent change back (debounced) via the *
 * mutation below.                                                  *
 * --------------------------------------------------------------- */

export interface UiSettingsResponse {
  json: string | null;
}

export function useUiSettings() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ['ui-settings', userId],
    queryFn: () =>
      api.get<UiSettingsResponse>(`/api/obd/user/${userId}/ui/settings`),
    enabled: Boolean(userId),
    // Settings change rarely; let tanstack-query refetch on remount but not
    // poll. Pulling once per login is the intended cadence.
    staleTime: Infinity,
    retry: false,
  });
}

export function useSaveUiSettings() {
  const userId = useAuthStore((s) => s.userId);
  return useMutation({
    mutationFn: (json: string) =>
      api.post<{ message?: string }>(
        `/api/obd/user/${userId}/ui/settings`,
        { json },
      ),
  });
}

/* --------------------------------------------------------------- *
 * Logout                                                           *
 * --------------------------------------------------------------- */

/**
 * Logout is a pure client operation against java_backend (no server
 * endpoint). The future obd3-api will gain POST /api/v3/auth/logout
 * which clears the httpOnly cookie server-side — when that exists,
 * this mutation will POST first, then clear local state.
 */
export function useLogoutMutation() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/api/obd/logout');
      } catch {
        // Silent — even if server logout fails, clear local state
      }
      logout();
    },
  });
}

/* --------------------------------------------------------------- *
 * Reset password flow (3 steps)                                    *
 *                                                                  *
 *   1. POST /api/obd/sendOtp/number   { username }                 *
 *      success -> "OTP Sent to your registered Mobile number"      *
 *                                                                  *
 *   2. POST /api/obd/verify/otp       { username, otp }            *
 *      success -> "OTP Verification Successfull" + { userId }      *
 *      (note: the "Successfull" misspelling is the wire format)    *
 *                                                                  *
 *   3. POST /api/obd/user/pwd/reset   { password, userId }         *
 *      success -> "Password Reset Successfully"                    *
 *                                                                  *
 * All endpoints return 200 with `{ message: ... }` on errors, so   *
 * the mutationFn must inspect the message and throw manually.      *
 * --------------------------------------------------------------- */

interface OtpResponse {
  message?: string;
  userId?: string;
}

const SEND_OTP_SUCCESS = 'OTP Sent to your registered Mobile number';
const VERIFY_OTP_SUCCESS = 'OTP Verification Successfull'; // wire-format typo preserved
const PWD_RESET_SUCCESS = 'Password Reset Successfully';

function postPublic<T>(url: string, body: unknown): Promise<T> {
  return api.post<T>(url, body, {
    transformRequest: [
      (data, headers) => {
        if (headers) delete (headers as Record<string, unknown>).Authorization;
        return JSON.stringify(data);
      },
    ],
  });
}

export function useSendOtpMutation() {
  return useMutation({
    mutationFn: async ({ username }: { username: string }) => {
      const res = await postPublic<OtpResponse>('/api/obd/sendOtp/number', { username });
      if (res.message !== SEND_OTP_SUCCESS) {
        throw new Error(res.message ?? 'Could not send OTP');
      }
      return res;
    },
  });
}

export function useVerifyOtpMutation() {
  return useMutation({
    mutationFn: async ({
      username,
      otp,
    }: {
      username: string;
      otp: string;
    }): Promise<{ userId: string }> => {
      const res = await postPublic<OtpResponse>('/api/obd/verify/otp', { username, otp });
      if (res.message !== VERIFY_OTP_SUCCESS || !res.userId) {
        throw new Error(res.message ?? 'OTP verification failed');
      }
      return { userId: res.userId };
    },
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      const res = await postPublic<{ message?: string }>('/api/obd/user/pwd/reset', {
        password,
        userId,
      });
      if (res.message !== PWD_RESET_SUCCESS) {
        throw new Error(res.message ?? 'Password reset failed');
      }
      return res;
    },
  });
}

/* --------------------------------------------------------------- *
 * Update password via email-link token.                            *
 *   POST /api/obd/update/pwd  { password, token }                  *
 *   success -> "Password Reset Successfully"                       *
 * --------------------------------------------------------------- */

export function useUpdatePasswordMutation() {
  return useMutation({
    mutationFn: async ({ token, password }: { token: string; password: string }) => {
      const res = await postPublic<{ message?: string }>('/api/obd/update/pwd', {
        password,
        token,
      });
      if (res.message !== PWD_RESET_SUCCESS) {
        throw new Error(res.message ?? 'Password update failed');
      }
      return res;
    },
  });
}
